/**
 * Integrytas Group - Common Security Module (CSM)
 * Page registry - the SINGLE SOURCE OF TRUTH for navigable pages.
 *
 * The shape mirrors the JSONB contract of public.csm_sync_page_master(p_pages).
 * On admin login the app calls that RPC with PAGE_REGISTRY so csm_page_master and
 * csm_page_master_tl are kept in lockstep with the codebase: new entries inserted,
 * updated entries refreshed, missing entries soft-deactivated.
 *
 * Menu taxonomy (v15): ten logical macro-groups; every page with a component in
 * PAGE_COMPONENTS appears exactly once. Pages absent here are soft-deactivated.
 */

import { APP_PAGE_REGISTRY } from '../app/app_pages';
import { PAGE_VERSIONS } from './page_versions.generated';

export interface PageTranslation {
  locale_code: string;
  display_text: string;
  description?: string;
}

export interface PageDefinition {
  internal_code: string;
  route_path: string;
  parent_internal_code?: string;
  sort_order: number;
  requires_signoff?: 'Y' | 'N';
  signoff_count?: number;
  is_active?: 'Y' | 'N';
  /**
   * 'Y' = the mobile app shows the "Desktop Version Required" modal instead of
   * routing to this page (the desktop route stays intact). 'N'/omitted (default)
   * = available on mobile. Cascades to descendants in the mobile menu (a page is
   * treated as desktop-only if its own flag OR any ancestor's is 'Y'). Left
   * UNCHECKED for all entries by default — set 'Y' on the pages (or parent NAV
   * groups) that should be desktop-only.
   */
  desktop_only?: 'Y' | 'N';
  /**
   * The application this page belongs to (csm_application_master.internal_code).
   * Omitted / absent = 'BASE' (the platform itself). Pages from other applications
   * carry that application's code so csm_sync_page_master resolves the right
   * application_id. Registry pages are BaseApp's own, so this is normally left off.
   */
  application_internal_code?: string;
  version_number?: string;
  release_version_number?: string;
  release_date?: string;
  translations: PageTranslation[];
}

const RAW_PAGE_REGISTRY: PageDefinition[] = [
  // ===== Macro-group nodes =====
  {
    internal_code: 'NAV.SECURITY',
    route_path:    '/security',
    parent_internal_code: 'NAV.SETTINGS',
    sort_order:    100,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Security & Access' },
      { locale_code: 'EN_GB', display_text: 'Security & Access' },
      { locale_code: 'ES_MX', display_text: 'Seguridad y Acceso' },
    ],
  },
  {
    internal_code: 'NAV.ORGANIZATION',
    route_path:    '/organization',
    parent_internal_code: 'NAV.SETTINGS',
    sort_order:    200,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Organization' },
      { locale_code: 'EN_GB', display_text: 'Organisation' },
      { locale_code: 'ES_MX', display_text: 'Organización' },
    ],
  },
  {
    internal_code: 'NAV.WORKFLOW',
    route_path:    '/workflow',
    parent_internal_code: 'NAV.SETTINGS',
    sort_order:    300,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Workflow & Approvals' },
      { locale_code: 'EN_GB', display_text: 'Workflow & Approvals' },
      { locale_code: 'ES_MX', display_text: 'Flujo y Aprobaciones' },
    ],
  },
  {
    internal_code: 'NAV.COMMUNICATIONS',
    route_path:    '/communications',
    parent_internal_code: 'NAV.SETTINGS',
    sort_order:    400,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Communications' },
      { locale_code: 'EN_GB', display_text: 'Communications' },
      { locale_code: 'ES_MX', display_text: 'Comunicaciones' },
    ],
  },
  {
    internal_code: 'NAV.COMMUNICATIONS.EDS',
    route_path:    '/communications/eds',
    parent_internal_code: 'NAV.COMMUNICATIONS',
    sort_order:    470,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Email Distribution' },
      { locale_code: 'EN_GB', display_text: 'Email Distribution' },
      { locale_code: 'ES_MX', display_text: 'Distribución de Correo' },
    ],
  },
  {
    internal_code: 'NAV.CONTENT',
    route_path:    '/content',
    parent_internal_code: 'NAV.SETTINGS',
    sort_order:    500,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Content & Documents' },
      { locale_code: 'EN_GB', display_text: 'Content & Documents' },
      { locale_code: 'ES_MX', display_text: 'Contenido y Documentos' },
    ],
  },
  {
    internal_code: 'NAV.CATALOG',
    route_path:    '/catalog-admin',
    sort_order:    1150,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Product Catalogue (Demo)' },
      { locale_code: 'EN_GB', display_text: 'Product Catalogue (Demo)' },
      { locale_code: 'ES_MX', display_text: 'Catálogo de Productos (Demo)' },
    ],
  },
  {
    internal_code: 'NAV.GOVERNANCE',
    route_path:    '/governance',
    parent_internal_code: 'NAV.SETTINGS',
    sort_order:    600,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Data Governance' },
      { locale_code: 'EN_GB', display_text: 'Data Governance' },
      { locale_code: 'ES_MX', display_text: 'Gobernanza de Datos' },
    ],
  },
  {
    internal_code: 'NAV.OPERATIONS',
    route_path:    '/operations',
    parent_internal_code: 'NAV.SETTINGS',
    sort_order:    700,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Platform Operations' },
      { locale_code: 'EN_GB', display_text: 'Platform Operations' },
      { locale_code: 'ES_MX', display_text: 'Operaciones de Plataforma' },
    ],
  },
  {
    internal_code: 'NAV.BUILD',
    route_path:    '/build',
    parent_internal_code: 'NAV.SETTINGS',
    sort_order:    800,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Customization & Low-Code' },
      { locale_code: 'EN_GB', display_text: 'Customisation & Low-Code' },
      { locale_code: 'ES_MX', display_text: 'Personalización y Low-Code' },
    ],
  },
  {
    internal_code: 'NAV.INTEGRATION',
    route_path:    '/integration',
    parent_internal_code: 'NAV.SETTINGS',
    sort_order:    900,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Integration & Sync' },
      { locale_code: 'EN_GB', display_text: 'Integration & Sync' },
      { locale_code: 'ES_MX', display_text: 'Integración y Sincronización' },
    ],
  },
  {
    internal_code: 'NAV.CONFIG',
    route_path:    '/configuration',
    parent_internal_code: 'NAV.SETTINGS',
    sort_order:    1000,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'System Configuration' },
      { locale_code: 'EN_GB', display_text: 'System Configuration' },
      { locale_code: 'ES_MX', display_text: 'Configuración del Sistema' },
    ],
  },
  {
    // Base Tables — the home for lookup / reference-code maintenance pages
    // (every code that backs a dropdown or status gets a table + a CRUD page,
    // per CLAUDE.md §17). BaseApp defines the macro-group; derived apps populate
    // it with their lookup-maintenance screens. It stays hidden from the menu
    // until a viewable page is registered under it (the menu nests only
    // can_view nodes — see §6), so an empty group in BaseApp is inert.
    internal_code: 'NAV.BASE_TABLES',
    route_path:    '/base-tables',
    sort_order:    1100,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Base Tables' },
      { locale_code: 'EN_GB', display_text: 'Base Tables' },
      { locale_code: 'ES_MX', display_text: 'Tablas Base' },
    ],
  },
  {
    // Platform — sub-group under Base Tables for BaseApp's own lookup pages
    // (currencies, exchange rates, document formats). Keeps Base Tables showing
    // only contracted sub-groups when expanded — a derived app adds its own
    // business-domain sub-groups alongside (§17.E) and may override individual
    // platform lookups into them (e.g. currencies under its Finance sub-group).
    internal_code: 'NAV.BASE_TABLES.PLATFORM',
    route_path:    '/base-tables/platform',
    parent_internal_code: 'NAV.BASE_TABLES',
    sort_order:    1105,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Platform' },
      { locale_code: 'EN_GB', display_text: 'Platform' },
      { locale_code: 'ES_MX', display_text: 'Plataforma' },
    ],
  },
  {
    // Settings — bottom-of-menu home for every platform admin group above.
    // BaseApp ships the demoted layout natively (the ten macro-groups declare
    // parent_internal_code: 'NAV.SETTINGS'), so derived apps inherit a menu
    // where platform chrome is already tucked away and only their own business
    // domains + Base Tables + Settings sit at the root (§17.E). NAV.CATALOG
    // (the DMO demo module) deliberately stays a top-level root here — it plays
    // the "business domain" role for anyone evaluating BaseApp directly, and
    // new_app.sh overrides it under Settings (inactive) for every real app.
    internal_code: 'NAV.SETTINGS',
    route_path:    '/settings',
    sort_order:    9000,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Settings' },
      { locale_code: 'EN_GB', display_text: 'Settings' },
      { locale_code: 'ES_MX', display_text: 'Configuración' },
    ],
  },

  // ===== Pages =====

  // DMO (Demo Product Catalog) — BaseApp's own reference module, isolated in
  // its own file so a derived app can delete it cleanly (§16); scripts/new_app.sh
  // does this automatically for every new app.

  {
    internal_code:        'CSM.USER.LIST',
    route_path:           '/security/users',
    parent_internal_code: 'NAV.SECURITY',
    sort_order:           110,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Users',
        description: 'Maintain users, activate accounts, and assign roles.' },
      { locale_code: 'EN_GB', display_text: 'Users',
        description: 'Maintain users, activate accounts, and assign roles.' },
      { locale_code: 'ES_MX', display_text: 'Usuarios' },
    ],
  },
  {
    internal_code:        'CSM.ROLE.LIST',
    route_path:           '/security/roles',
    parent_internal_code: 'NAV.SECURITY',
    sort_order:           120,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Roles',
        description: 'Maintain roles and the page-access matrix.' },
      { locale_code: 'EN_GB', display_text: 'Roles',
        description: 'Maintain roles and the page-access matrix.' },
      { locale_code: 'ES_MX', display_text: 'Roles' },
    ],
  },
  {
    internal_code:        'CSM.PAGE.MASTER',
    route_path:           '/security/pages',
    parent_internal_code: 'NAV.SECURITY',
    sort_order:           130,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Pages',
        description: 'Maintain the application menu and page hierarchy.' },
      { locale_code: 'EN_GB', display_text: 'Pages',
        description: 'Maintain the application menu and page hierarchy.' },
      { locale_code: 'ES_MX', display_text: 'Páginas' },
    ],
  },
  {
    internal_code:        'CSM.JOB.LIST',
    route_path:           '/security/jobs',
    parent_internal_code: 'NAV.SECURITY',
    sort_order:           140,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Job Descriptions',
        description: 'Define job descriptions used for provisioning and approvals.' },
      { locale_code: 'EN_GB', display_text: 'Job Descriptions',
        description: 'Define job descriptions used for provisioning and approvals.' },
      { locale_code: 'ES_MX', display_text: 'Descripciones de Puesto' },
    ],
  },
  {
    internal_code:        'CSM.HIERARCHY.LIST',
    route_path:           '/security/approval-hierarchies',
    parent_internal_code: 'NAV.SECURITY',
    sort_order:           150,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Approval Hierarchies',
        description: 'Maintain approval hierarchies and reporting lines.' },
      { locale_code: 'EN_GB', display_text: 'Approval Hierarchies',
        description: 'Maintain approval hierarchies and reporting lines.' },
      { locale_code: 'ES_MX', display_text: 'Jerarquías de Aprobación' },
    ],
  },
  {
    internal_code:        'IAM.PROVIDERS',
    route_path:           '/iam/providers',
    parent_internal_code: 'NAV.SECURITY',
    sort_order:           160,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Identity Providers',
        description: 'Configure external identity / SSO providers.' },
      { locale_code: 'EN_GB', display_text: 'Identity Providers',
        description: 'Configure external identity / SSO providers.' },
      { locale_code: 'ES_MX', display_text: 'Proveedores de Identidad' },
    ],
  },
  {
    internal_code:        'CSM.USER.PROVISION',
    route_path:           '/workflow/provision',
    parent_internal_code: 'NAV.SECURITY',
    sort_order:           170,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'User Provisioning',
        description: 'Provision and onboard new user accounts.' },
      { locale_code: 'EN_GB', display_text: 'User Provisioning',
        description: 'Provision and onboard new user accounts.' },
      { locale_code: 'ES_MX', display_text: 'Aprovisionamiento de Usuarios' },
    ],
  },
  {
    internal_code:        'CSM.ORG.COMPANY',
    route_path:           '/organization/companies',
    parent_internal_code: 'NAV.ORGANIZATION',
    sort_order:           210,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Companies',
        description: 'Maintain companies (the org root).' },
      { locale_code: 'EN_GB', display_text: 'Companies',
        description: 'Maintain companies (the org root).' },
      { locale_code: 'ES_MX', display_text: 'Compañías' },
    ],
  },
  {
    internal_code:        'CSM.ORG.DIVISION',
    route_path:           '/organization/divisions',
    parent_internal_code: 'NAV.ORGANIZATION',
    sort_order:           220,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Divisions',
        description: 'Maintain divisions within companies.' },
      { locale_code: 'EN_GB', display_text: 'Divisions',
        description: 'Maintain divisions within companies.' },
      { locale_code: 'ES_MX', display_text: 'Divisiones' },
    ],
  },
  {
    internal_code:        'CSM.ORG.BUSINESS_UNIT',
    route_path:           '/organization/business-units',
    parent_internal_code: 'NAV.ORGANIZATION',
    sort_order:           230,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Business Units',
        description: 'Maintain business units within divisions.' },
      { locale_code: 'EN_GB', display_text: 'Business Units',
        description: 'Maintain business units within divisions.' },
      { locale_code: 'ES_MX', display_text: 'Unidades de Negocio' },
    ],
  },
  {
    internal_code:        'CSM.ORG.OFFICE',
    route_path:           '/organization/offices',
    parent_internal_code: 'NAV.ORGANIZATION',
    sort_order:           240,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Offices',
        description: 'Maintain offices / locations.' },
      { locale_code: 'EN_GB', display_text: 'Offices',
        description: 'Maintain offices / locations.' },
      { locale_code: 'ES_MX', display_text: 'Oficinas' },
    ],
  },
  {
    internal_code:        'APR.INBOX',
    route_path:           '/apr/inbox',
    parent_internal_code: 'NAV.WORKFLOW',
    sort_order:           310,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'My Approvals',
        description: 'Review and action items awaiting your approval.' },
      { locale_code: 'EN_GB', display_text: 'My Approvals',
        description: 'Review and action items awaiting your approval.' },
      { locale_code: 'ES_MX', display_text: 'Mis Aprobaciones' },
    ],
  },
  {
    internal_code:        'APR.DELEGATIONS',
    route_path:           '/apr/delegations',
    parent_internal_code: 'NAV.WORKFLOW',
    sort_order:           320,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Delegations',
        description: 'Delegate approval authority during absences.' },
      { locale_code: 'EN_GB', display_text: 'Delegations',
        description: 'Delegate approval authority during absences.' },
      { locale_code: 'ES_MX', display_text: 'Delegaciones' },
    ],
  },
  {
    internal_code:        'APR.CONFIG',
    route_path:           '/apr/config',
    parent_internal_code: 'NAV.WORKFLOW',
    sort_order:           330,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Approval Configuration',
        description: 'Configure approval rules per entity and operation.' },
      { locale_code: 'EN_GB', display_text: 'Approval Configuration',
        description: 'Configure approval rules per entity and operation.' },
      { locale_code: 'ES_MX', display_text: 'Configuración de Aprobaciones' },
    ],
  },
  {
    internal_code:        'APR.SLA_AUTHORITY',
    route_path:           '/apr/sla-authority',
    parent_internal_code: 'NAV.WORKFLOW',
    sort_order:           340,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'SLA & Authority',
        description: 'Approval SLA targets and authority limits.' },
      { locale_code: 'EN_GB', display_text: 'SLA & Authority',
        description: 'Approval SLA targets and authority limits.' },
      { locale_code: 'ES_MX', display_text: 'SLA y Autoridad' },
    ],
  },
  {
    internal_code:        'APR.ANALYTICS',
    route_path:           '/apr/analytics',
    parent_internal_code: 'NAV.WORKFLOW',
    sort_order:           350,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Approval Analytics',
        description: 'Throughput, bottlenecks, and SLA breach analytics.' },
      { locale_code: 'EN_GB', display_text: 'Approval Analytics',
        description: 'Throughput, bottlenecks, and SLA breach analytics.' },
      { locale_code: 'ES_MX', display_text: 'Analítica de Aprobaciones' },
    ],
  },
  {
    internal_code:        'UNE.ADMIN',
    route_path:           '/une/admin',
    parent_internal_code: 'NAV.COMMUNICATIONS',
    sort_order:           410,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Notification Admin',
        description: 'Manage notification categories and channels.' },
      { locale_code: 'EN_GB', display_text: 'Notification Admin',
        description: 'Manage notification categories and channels.' },
      { locale_code: 'ES_MX', display_text: 'Administración de Notificaciones' },
    ],
  },
  {
    internal_code:        'UNE.PREFERENCES',
    route_path:           '/une/preferences',
    parent_internal_code: 'NAV.COMMUNICATIONS',
    sort_order:           420,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Notification Preferences',
        description: 'Per-user notification preferences.' },
      { locale_code: 'EN_GB', display_text: 'Notification Preferences',
        description: 'Per-user notification preferences.' },
      { locale_code: 'ES_MX', display_text: 'Preferencias de Notificación' },
    ],
  },
  {
    internal_code:        'MTA.RULES',
    route_path:           '/mta/rules',
    parent_internal_code: 'NAV.COMMUNICATIONS',
    sort_order:           430,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Business Events',
        description: 'Automation rules: messaging and task triggers.' },
      { locale_code: 'EN_GB', display_text: 'Business Events',
        description: 'Automation rules: messaging and task triggers.' },
      { locale_code: 'ES_MX', display_text: 'Eventos de Negocio' },
    ],
  },
  {
    internal_code:        'MTA.TASKS',
    route_path:           '/mta/tasks',
    parent_internal_code: 'NAV.COMMUNICATIONS',
    sort_order:           440,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Tasks',
        description: 'Generated tasks and their status.' },
      { locale_code: 'EN_GB', display_text: 'Tasks',
        description: 'Generated tasks and their status.' },
      { locale_code: 'ES_MX', display_text: 'Tareas' },
    ],
  },
  {
    internal_code:        'ECM.TEMPLATES',
    route_path:           '/ecm/templates',
    parent_internal_code: 'NAV.COMMUNICATIONS',
    sort_order:           450,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Email Templates',
        description: 'Transactional, record-driven communication templates.' },
      { locale_code: 'EN_GB', display_text: 'Email Templates',
        description: 'Transactional, record-driven communication templates.' },
      { locale_code: 'ES_MX', display_text: 'Plantillas de Correo' },
    ],
  },
  {
    internal_code:        'DOC.TEMPLATES',
    route_path:           '/doc/templates',
    parent_internal_code: 'NAV.COMMUNICATIONS',
    sort_order:           460,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Document Generation',
        description: 'Document/report templates with token binding.' },
      { locale_code: 'EN_GB', display_text: 'Document Generation',
        description: 'Document/report templates with token binding.' },
      { locale_code: 'ES_MX', display_text: 'Generación de Documentos' },
    ],
  },
  {
    internal_code:        'DOC.GENERATED',
    route_path:           '/doc/generated',
    parent_internal_code: 'NAV.COMMUNICATIONS',
    sort_order:           465,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Generated Documents',
        description: 'Render queue and status for generated documents and reports.' },
      { locale_code: 'EN_GB', display_text: 'Generated Documents',
        description: 'Render queue and status for generated documents and reports.' },
      { locale_code: 'ES_MX', display_text: 'Documentos Generados' },
    ],
  },
  {
    internal_code:        'RPD.DESIGNER',
    route_path:           '/rpd/designer',
    parent_internal_code: 'NAV.COMMUNICATIONS',
    sort_order:           467,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Report Designer',
        description: 'Visual banded-report designer with live preview.' },
      { locale_code: 'EN_GB', display_text: 'Report Designer',
        description: 'Visual banded-report designer with live preview.' },
      { locale_code: 'ES_MX', display_text: 'Diseñador de Informes' },
    ],
  },
  {
    internal_code:        'EDS.DASHBOARD',
    route_path:           '/eds/dashboard',
    parent_internal_code: 'NAV.COMMUNICATIONS.EDS',
    sort_order:           471,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Email Dashboard',
        description: 'Email distribution overview and metrics.' },
      { locale_code: 'EN_GB', display_text: 'Email Dashboard',
        description: 'Email distribution overview and metrics.' },
      { locale_code: 'ES_MX', display_text: 'Panel de Correo' },
    ],
  },
  {
    internal_code:        'EDS.TEMPLATE.LIST',
    route_path:           '/eds/templates',
    parent_internal_code: 'NAV.COMMUNICATIONS.EDS',
    sort_order:           472,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Campaign Templates',
        description: 'Marketing email templates.' },
      { locale_code: 'EN_GB', display_text: 'Campaign Templates',
        description: 'Marketing email templates.' },
      { locale_code: 'ES_MX', display_text: 'Plantillas de Campaña' },
    ],
  },
  {
    internal_code:        'EDS.CAMPAIGN.LIST',
    route_path:           '/eds/campaigns',
    parent_internal_code: 'NAV.COMMUNICATIONS.EDS',
    sort_order:           473,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Campaigns',
        description: 'Bulk email campaigns to recipient lists.' },
      { locale_code: 'EN_GB', display_text: 'Campaigns',
        description: 'Bulk email campaigns to recipient lists.' },
      { locale_code: 'ES_MX', display_text: 'Campañas' },
    ],
  },
  {
    internal_code:        'EDS.RECIPIENT.LIST',
    route_path:           '/eds/recipients',
    parent_internal_code: 'NAV.COMMUNICATIONS.EDS',
    sort_order:           474,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Recipients',
        description: 'Recipient lists and subscribers.' },
      { locale_code: 'EN_GB', display_text: 'Recipients',
        description: 'Recipient lists and subscribers.' },
      { locale_code: 'ES_MX', display_text: 'Destinatarios' },
    ],
  },
  {
    internal_code:        'EDS.EMAIL.LOG',
    route_path:           '/eds/log',
    parent_internal_code: 'NAV.COMMUNICATIONS.EDS',
    sort_order:           475,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Email Log',
        description: 'Delivery, open, click, and bounce events.' },
      { locale_code: 'EN_GB', display_text: 'Email Log',
        description: 'Delivery, open, click, and bounce events.' },
      { locale_code: 'ES_MX', display_text: 'Registro de Correo' },
    ],
  },
  {
    internal_code:        'EDS.OPT.OUT',
    route_path:           '/eds/opt-out',
    parent_internal_code: 'NAV.COMMUNICATIONS.EDS',
    sort_order:           476,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Opt-Out Management',
        description: 'Unsubscribe and opt-out compliance.' },
      { locale_code: 'EN_GB', display_text: 'Opt-Out Management',
        description: 'Unsubscribe and opt-out compliance.' },
      { locale_code: 'ES_MX', display_text: 'Gestión de Bajas' },
    ],
  },
  {
    internal_code:        'CMS.PAGE.LIST',
    route_path:           '/cms/pages',
    parent_internal_code: 'NAV.CONTENT',
    sort_order:           510,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'CMS Pages',
        description: 'Author content pages for portals/sites.' },
      { locale_code: 'EN_GB', display_text: 'CMS Pages',
        description: 'Author content pages for portals/sites.' },
      { locale_code: 'ES_MX', display_text: 'Páginas CMS' },
    ],
  },
  {
    internal_code:        'CMS.ASSET.LIBRARY',
    route_path:           '/cms/assets',
    parent_internal_code: 'NAV.CONTENT',
    sort_order:           520,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Asset Library',
        description: 'Manage images, video, and content assets.' },
      { locale_code: 'EN_GB', display_text: 'Asset Library',
        description: 'Manage images, video, and content assets.' },
      { locale_code: 'ES_MX', display_text: 'Biblioteca de Recursos' },
    ],
  },
  {
    internal_code:        'DMS.DOCUMENTS',
    route_path:           '/documents/manage',
    parent_internal_code: 'NAV.CONTENT',
    sort_order:           530,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Documents',
        description: 'Document management and storage.' },
      { locale_code: 'EN_GB', display_text: 'Documents',
        description: 'Document management and storage.' },
      { locale_code: 'ES_MX', display_text: 'Documentos' },
    ],
  },
  {
    internal_code:        'OLH.MANAGER',
    route_path:           '/olh/manager',
    parent_internal_code: 'NAV.CONTENT',
    sort_order:           540,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Online Help',
        description: 'Contextual online help content.' },
      { locale_code: 'EN_GB', display_text: 'Online Help',
        description: 'Contextual online help content.' },
      { locale_code: 'ES_MX', display_text: 'Ayuda en Línea' },
    ],
  },
  {
    internal_code:        'DQE.RULES',
    route_path:           '/dqe/rules',
    parent_internal_code: 'NAV.GOVERNANCE',
    sort_order:           610,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Data Quality Rules',
        description: 'Validation rules enforced on every write.' },
      { locale_code: 'EN_GB', display_text: 'Data Quality Rules',
        description: 'Validation rules enforced on every write.' },
      { locale_code: 'ES_MX', display_text: 'Reglas de Calidad de Datos' },
    ],
  },
  {
    internal_code:        'DPC.POLICY',
    route_path:           '/dpc/policy',
    parent_internal_code: 'NAV.GOVERNANCE',
    sort_order:           620,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Privacy Policy',
        description: 'Field masking, purposes, and subject map.' },
      { locale_code: 'EN_GB', display_text: 'Privacy Policy',
        description: 'Field masking, purposes, and subject map.' },
      { locale_code: 'ES_MX', display_text: 'Política de Privacidad' },
    ],
  },
  {
    internal_code:        'DPC.REQUESTS',
    route_path:           '/dpc/requests',
    parent_internal_code: 'NAV.GOVERNANCE',
    sort_order:           630,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Privacy Requests',
        description: 'DSAR gather/erase requests.' },
      { locale_code: 'EN_GB', display_text: 'Privacy Requests',
        description: 'DSAR gather/erase requests.' },
      { locale_code: 'ES_MX', display_text: 'Solicitudes de Privacidad' },
    ],
  },
  {
    internal_code:        'ADI.WATCHDOG',
    route_path:           '/adi/watchdog',
    parent_internal_code: 'NAV.GOVERNANCE',
    sort_order:           640,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Integrity Watchdog',
        description: 'Data-integrity assertions and alerts.' },
      { locale_code: 'EN_GB', display_text: 'Integrity Watchdog',
        description: 'Data-integrity assertions and alerts.' },
      { locale_code: 'ES_MX', display_text: 'Vigilante de Integridad' },
    ],
  },
  {
    internal_code:        'UAT.TIMELINE',
    route_path:           '/uat/timeline',
    parent_internal_code: 'NAV.GOVERNANCE',
    sort_order:           650,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Audit Trail',
        description: 'Unified audit trail timeline per record.' },
      { locale_code: 'EN_GB', display_text: 'Audit Trail',
        description: 'Unified audit trail timeline per record.' },
      { locale_code: 'ES_MX', display_text: 'Pista de Auditoría' },
    ],
  },
  {
    internal_code:        'CSM.SYSTEM.AUDIT_LOG',
    route_path:           '/system/audit-log',
    parent_internal_code: 'NAV.GOVERNANCE',
    sort_order:           660,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Audit Log',
        description: 'Raw audit log browser.' },
      { locale_code: 'EN_GB', display_text: 'Audit Log',
        description: 'Raw audit log browser.' },
      { locale_code: 'ES_MX', display_text: 'Registro de Auditoría' },
    ],
  },
  {
    internal_code:        'GTC.TRASH',
    route_path:           '/gtc/trash',
    parent_internal_code: 'NAV.GOVERNANCE',
    sort_order:           670,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Trash Can',
        description: 'Recover or purge soft-deleted records.' },
      { locale_code: 'EN_GB', display_text: 'Trash Can',
        description: 'Recover or purge soft-deleted records.' },
      { locale_code: 'ES_MX', display_text: 'Papelera' },
    ],
  },
  {
    internal_code:        'DAP.POLICIES',
    route_path:           '/dap/policies',
    parent_internal_code: 'NAV.GOVERNANCE',
    sort_order:           680,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Archiving & Purging',
        description: 'Data archiving (cold tier) and purge policies.' },
      { locale_code: 'EN_GB', display_text: 'Archiving & Purging',
        description: 'Data archiving (cold tier) and purge policies.' },
      { locale_code: 'ES_MX', display_text: 'Archivado y Purga' },
    ],
  },
  {
    internal_code:        'PDE.DASHBOARD',
    route_path:           '/pde/dashboard',
    parent_internal_code: 'NAV.OPERATIONS',
    sort_order:           710,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Performance Diagnostics',
        description: 'Performance diagnostics dashboard.' },
      { locale_code: 'EN_GB', display_text: 'Performance Diagnostics',
        description: 'Performance diagnostics dashboard.' },
      { locale_code: 'ES_MX', display_text: 'Diagnóstico de Rendimiento' },
    ],
  },
  {
    internal_code:        'APG.TELEMETRY',
    route_path:           '/apg/telemetry',
    parent_internal_code: 'NAV.OPERATIONS',
    sort_order:           720,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Performance Guardrail',
        description: 'Slow-call telemetry and thresholds.' },
      { locale_code: 'EN_GB', display_text: 'Performance Guardrail',
        description: 'Slow-call telemetry and thresholds.' },
      { locale_code: 'ES_MX', display_text: 'Barrera de Rendimiento' },
    ],
  },
  {
    internal_code:        'ARL.CONFIG',
    route_path:           '/arl/config',
    parent_internal_code: 'NAV.OPERATIONS',
    sort_order:           730,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Rate Limiting',
        description: 'API rate-limit rules.' },
      { locale_code: 'EN_GB', display_text: 'Rate Limiting',
        description: 'API rate-limit rules.' },
      { locale_code: 'ES_MX', display_text: 'Límite de Tasa' },
    ],
  },
  {
    internal_code:        'CBM.BREAKERS',
    route_path:           '/cbm/breakers',
    parent_internal_code: 'NAV.OPERATIONS',
    sort_order:           740,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Circuit Breakers',
        description: 'Outbound third-party circuit breakers.' },
      { locale_code: 'EN_GB', display_text: 'Circuit Breakers',
        description: 'Outbound third-party circuit breakers.' },
      { locale_code: 'ES_MX', display_text: 'Disyuntores' },
    ],
  },
  {
    internal_code:        'IKE.KEYS',
    route_path:           '/ike/keys',
    parent_internal_code: 'NAV.OPERATIONS',
    sort_order:           750,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Idempotency Keys',
        description: 'Gateway idempotency key ledger.' },
      { locale_code: 'EN_GB', display_text: 'Idempotency Keys',
        description: 'Gateway idempotency key ledger.' },
      { locale_code: 'ES_MX', display_text: 'Claves de Idempotencia' },
    ],
  },
  {
    internal_code:        'ROR.ROUTING',
    route_path:           '/ror/routing',
    parent_internal_code: 'NAV.OPERATIONS',
    sort_order:           760,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Read Routing',
        description: 'Read-optimization routing rules.' },
      { locale_code: 'EN_GB', display_text: 'Read Routing',
        description: 'Read-optimisation routing rules.' },
      { locale_code: 'ES_MX', display_text: 'Enrutamiento de Lectura' },
    ],
  },
  {
    internal_code:        'CMM.LOCKS',
    route_path:           '/cmm/locks',
    parent_internal_code: 'NAV.OPERATIONS',
    sort_order:           770,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Concurrency Locks',
        description: 'Active soft-locks and concurrency control.' },
      { locale_code: 'EN_GB', display_text: 'Concurrency Locks',
        description: 'Active soft-locks and concurrency control.' },
      { locale_code: 'ES_MX', display_text: 'Bloqueos de Concurrencia' },
    ],
  },
  {
    internal_code:        'ASF.DIAGNOSTICS',
    route_path:           '/asf/diagnostics',
    parent_internal_code: 'NAV.OPERATIONS',
    sort_order:           780,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Session Diagnostics',
        description: 'App-state / session fingerprint diagnostics.' },
      { locale_code: 'EN_GB', display_text: 'Session Diagnostics',
        description: 'App-state / session fingerprint diagnostics.' },
      { locale_code: 'ES_MX', display_text: 'Diagnóstico de Sesión' },
    ],
  },
  {
    internal_code:        'GSI.INDEXER',
    route_path:           '/gsi/indexer',
    parent_internal_code: 'NAV.OPERATIONS',
    sort_order:           790,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Global Search Index',
        description: 'Audit-driven global search index.' },
      { locale_code: 'EN_GB', display_text: 'Global Search Index',
        description: 'Audit-driven global search index.' },
      { locale_code: 'ES_MX', display_text: 'Índice de Búsqueda Global' },
    ],
  },
  {
    internal_code:        'DDM.REFRESH',
    route_path:           '/ddm/refresh',
    parent_internal_code: 'NAV.OPERATIONS',
    sort_order:           800,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Refresh Demo Database',
        description: 'Refresh the demo schema with the latest production data.' },
      { locale_code: 'EN_GB', display_text: 'Refresh Demo Database',
        description: 'Refresh the demo schema with the latest production data.' },
      { locale_code: 'ES_MX', display_text: 'Actualizar Base de Datos Demo' },
    ],
  },
  {
    internal_code:        'DSE.REGISTRY',
    route_path:           '/dse/registry',
    parent_internal_code: 'NAV.BUILD',
    sort_order:           810,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Shadow-Field Registry',
        description: 'Define JSONB shadow-field schemas per entity.' },
      { locale_code: 'EN_GB', display_text: 'Shadow-Field Registry',
        description: 'Define JSONB shadow-field schemas per entity.' },
      { locale_code: 'ES_MX', display_text: 'Registro de Campos Dinámicos' },
    ],
  },
  {
    internal_code:        'UDC.LAYOUT',
    route_path:           '/udc/layout',
    parent_internal_code: 'NAV.BUILD',
    sort_order:           820,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Screen Layout',
        description: 'Place custom fields into screens per workspace.' },
      { locale_code: 'EN_GB', display_text: 'Screen Layout',
        description: 'Place custom fields into screens per workspace.' },
      { locale_code: 'ES_MX', display_text: 'Diseño de Pantalla' },
    ],
  },
  {
    internal_code:        'DSE.REPORTING',
    route_path:           '/dse/reporting',
    parent_internal_code: 'NAV.BUILD',
    sort_order:           830,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Report Columns & Indexes',
        description: 'Expose custom columns to reports; index advisor.' },
      { locale_code: 'EN_GB', display_text: 'Report Columns & Indexes',
        description: 'Expose custom columns to reports; index advisor.' },
      { locale_code: 'ES_MX', display_text: 'Columnas de Reporte e Índices' },
    ],
  },
  {
    internal_code:        'CDS.DATASET.LIST',
    route_path:           '/cds/datasets',
    parent_internal_code: 'NAV.BUILD',
    sort_order:           835,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Datasets',
        description: 'Define reusable datasets to place on CMS pages and portals.' },
      { locale_code: 'EN_GB', display_text: 'Datasets',
        description: 'Define reusable datasets to place on CMS pages and portals.' },
      { locale_code: 'ES_MX', display_text: 'Conjuntos de Datos',
        description: 'Defina conjuntos de datos reutilizables para páginas CMS y portales.' },
    ],
  },
  {
    internal_code:        'ULD.OVERRIDES',
    route_path:           '/uld/overrides',
    parent_internal_code: 'NAV.BUILD',
    sort_order:           840,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Layout Overrides',
        description: 'Per-workspace screen layout deltas.' },
      { locale_code: 'EN_GB', display_text: 'Layout Overrides',
        description: 'Per-workspace screen layout deltas.' },
      { locale_code: 'ES_MX', display_text: 'Anulaciones de Diseño' },
    ],
  },
  {
    internal_code:        'PHE.HOOKS',
    route_path:           '/phe/hooks',
    parent_internal_code: 'NAV.BUILD',
    sort_order:           850,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Pipeline Hooks',
        description: 'Workspace BEFORE/INSTEAD/AFTER pipeline hooks.' },
      { locale_code: 'EN_GB', display_text: 'Pipeline Hooks',
        description: 'Workspace BEFORE/INSTEAD/AFTER pipeline hooks.' },
      { locale_code: 'ES_MX', display_text: 'Hooks de Pipeline' },
    ],
  },
  {
    internal_code:        'FDM.MAP',
    route_path:           '/fdm/map',
    parent_internal_code: 'NAV.BUILD',
    sort_order:           860,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Dependency Map',
        description: 'Config dependency map and breaking-change analyzer.' },
      { locale_code: 'EN_GB', display_text: 'Dependency Map',
        description: 'Config dependency map and breaking-change analyser.' },
      { locale_code: 'ES_MX', display_text: 'Mapa de Dependencias' },
    ],
  },
  {
    internal_code:        'DAD.EXPLORER',
    route_path:           '/dad/explorer',
    parent_internal_code: 'NAV.BUILD',
    sort_order:           870,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'API Explorer & Sandbox',
        description: 'Runtime OpenAPI docs and a test sandbox.' },
      { locale_code: 'EN_GB', display_text: 'API Explorer & Sandbox',
        description: 'Runtime OpenAPI docs and a test sandbox.' },
      { locale_code: 'ES_MX', display_text: 'Explorador de API' },
    ],
  },
  {
    internal_code:        'FLN.FIELD_SOURCE',
    route_path:           '/fln/field-source',
    parent_internal_code: 'NAV.BUILD',
    sort_order:           875,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Field Lineage',
        description: 'Document where each screen field’s data comes from (for report building).' },
      { locale_code: 'EN_GB', display_text: 'Field Lineage',
        description: 'Document where each screen field’s data comes from (for report building).' },
      { locale_code: 'ES_MX', display_text: 'Origen de Campos',
        description: 'Documente de dónde proviene el dato de cada campo (para crear informes).' },
    ],
  },
  {
    internal_code:        'DFF.FLAGS',
    route_path:           '/dff/flags',
    parent_internal_code: 'NAV.BUILD',
    sort_order:           880,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Feature Flags',
        description: 'Backend feature flags and rollout guards.' },
      { locale_code: 'EN_GB', display_text: 'Feature Flags',
        description: 'Backend feature flags and rollout guards.' },
      { locale_code: 'ES_MX', display_text: 'Banderas de Función' },
    ],
  },
  {
    internal_code:        'ISR.TARGETS',
    route_path:           '/isr/targets',
    parent_internal_code: 'NAV.INTEGRATION',
    sort_order:           910,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Inbound Sync',
        description: 'Inbound external polling and sync targets.' },
      { locale_code: 'EN_GB', display_text: 'Inbound Sync',
        description: 'Inbound external polling and sync targets.' },
      { locale_code: 'ES_MX', display_text: 'Sincronización Entrante' },
    ],
  },
  {
    internal_code:        'ODS.SYNC',
    route_path:           '/ods/sync',
    parent_internal_code: 'NAV.INTEGRATION',
    sort_order:           920,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Offline Sync',
        description: 'Operational data store / offline sync.' },
      { locale_code: 'EN_GB', display_text: 'Offline Sync',
        description: 'Operational data store / offline sync.' },
      { locale_code: 'ES_MX', display_text: 'Sincronización Sin Conexión' },
    ],
  },
  {
    internal_code:        'ODS.CONFLICTS',
    route_path:           '/ods/conflicts',
    parent_internal_code: 'NAV.INTEGRATION',
    sort_order:           930,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Sync Conflicts',
        description: 'Resolve offline-sync conflicts.' },
      { locale_code: 'EN_GB', display_text: 'Sync Conflicts',
        description: 'Resolve offline-sync conflicts.' },
      { locale_code: 'ES_MX', display_text: 'Conflictos de Sincronización' },
    ],
  },
  {
    internal_code:        'UPP.DEFINITIONS',
    route_path:           '/upp/definitions',
    parent_internal_code: 'NAV.INTEGRATION',
    sort_order:           940,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Import / Export',
        description: 'Unified import/export pipeline definitions.' },
      { locale_code: 'EN_GB', display_text: 'Import / Export',
        description: 'Unified import/export pipeline definitions.' },
      { locale_code: 'ES_MX', display_text: 'Importar / Exportar' },
    ],
  },
  {
    internal_code:        'PFW.PORTALS',
    route_path:           '/pfw/portals',
    parent_internal_code: 'NAV.INTEGRATION',
    sort_order:           950,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Portals',
        description: 'External portal framework configuration.' },
      { locale_code: 'EN_GB', display_text: 'Portals',
        description: 'External portal framework configuration.' },
      { locale_code: 'ES_MX', display_text: 'Portales' },
    ],
  },
  {
    internal_code:        'PFW.MEMBERSHIP',
    route_path:           '/pfw/membership',
    parent_internal_code: 'NAV.INTEGRATION',
    sort_order:           960,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Portal Membership',
        description: 'Portal user membership and access.' },
      { locale_code: 'EN_GB', display_text: 'Portal Membership',
        description: 'Portal user membership and access.' },
      { locale_code: 'ES_MX', display_text: 'Membresía de Portal' },
    ],
  },
  {
    internal_code:        'PFW.PORTAL_ADMIN',
    route_path:           '/pfw/portal-admin',
    parent_internal_code: 'NAV.INTEGRATION',
    sort_order:           965,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Portal Administration',
        description: 'Manage a third party’s portal roles and users.' },
      { locale_code: 'EN_GB', display_text: 'Portal Administration',
        description: 'Manage a third party’s portal roles and users.' },
      { locale_code: 'ES_MX', display_text: 'Administración del Portal',
        description: 'Gestione los roles y usuarios del portal de un tercero.' },
    ],
  },
  {
    internal_code:        'CSM.SYSTEM.BULK_IMPORT',
    route_path:           '/system/bulk-import',
    parent_internal_code: 'NAV.INTEGRATION',
    sort_order:           970,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Bulk Import',
        description: 'CSV/XLSX bulk import via the Common Routine.' },
      { locale_code: 'EN_GB', display_text: 'Bulk Import',
        description: 'CSV/XLSX bulk import via the Common Routine.' },
      { locale_code: 'ES_MX', display_text: 'Importación Masiva' },
    ],
  },
  {
    internal_code:        'CSM.SYSTEM.PARAMETERS',
    route_path:           '/system/parameters',
    parent_internal_code: 'NAV.CONFIG',
    sort_order:           1010,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'System Parameters',
        description: 'Key-value system parameters.' },
      { locale_code: 'EN_GB', display_text: 'System Parameters',
        description: 'Key-value system parameters.' },
      { locale_code: 'ES_MX', display_text: 'Parámetros del Sistema' },
    ],
  },
  {
    internal_code:        'CSM.I18N.MANAGER',
    route_path:           '/configuration/translations',
    parent_internal_code: 'NAV.CONFIG',
    sort_order:           1060,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Translations',
        description: 'Manage screen prompts and data translations across languages.' },
      { locale_code: 'EN_GB', display_text: 'Translations',
        description: 'Manage screen prompts and data translations across languages.' },
      { locale_code: 'ES_MX', display_text: 'Traducciones' },
    ],
  },
  {
    internal_code:        'CSM.I18N.NEW_LANGUAGE',
    route_path:           '/configuration/new-language',
    parent_internal_code: 'NAV.CONFIG',
    sort_order:           1070,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Create Language',
        description: 'Add a new language and seed its translations.' },
      { locale_code: 'EN_GB', display_text: 'Create Language',
        description: 'Add a new language and seed its translations.' },
      { locale_code: 'ES_MX', display_text: 'Crear Idioma' },
    ],
  },
  {
    internal_code:        'CSM.SYSTEM.AI_CONFIG',
    route_path:           '/admin/ai-config',
    parent_internal_code: 'NAV.CONFIG',
    sort_order:           1020,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'AI Configuration',
        description: 'AI provider and per-screen AI configuration.' },
      { locale_code: 'EN_GB', display_text: 'AI Configuration',
        description: 'AI provider and per-screen AI configuration.' },
      { locale_code: 'ES_MX', display_text: 'Configuración de IA' },
    ],
  },
  {
    internal_code:        'CSM.SYSTEM.ASSETS',
    route_path:           '/system/assets',
    parent_internal_code: 'NAV.CONFIG',
    sort_order:           1030,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Asset Upload',
        description: 'Upload branding and system assets.' },
      { locale_code: 'EN_GB', display_text: 'Asset Upload',
        description: 'Upload branding and system assets.' },
      { locale_code: 'ES_MX', display_text: 'Carga de Recursos' },
    ],
  },
  {
    internal_code:        'LIC.STATUS',
    route_path:           '/lic/status',
    parent_internal_code: 'NAV.CONFIG',
    sort_order:           1040,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'My Licence',
        description: 'Verified module entitlement for this tenant.' },
      { locale_code: 'EN_GB', display_text: 'My Licence',
        description: 'Verified module entitlement for this tenant.' },
      { locale_code: 'ES_MX', display_text: 'Mi Licencia' },
    ],
  },

  // CSM — Applications lookup (the application each page belongs to)
  {
    internal_code:        'CSM.APPLICATION.LIST',
    route_path:           '/base-tables/applications',
    parent_internal_code: 'NAV.BASE_TABLES.PLATFORM',
    sort_order:           1108,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Applications',
        description: 'Maintain the applications a page can belong to.' },
      { locale_code: 'EN_GB', display_text: 'Applications',
        description: 'Maintain the applications a page can belong to.' },
      { locale_code: 'ES_MX', display_text: 'Aplicaciones' },
    ],
  },

  // FXR — Foreign Exchange Rates (BaseApp core module)
  {
    internal_code:        'FXR.CURRENCY.LIST',
    route_path:           '/base-tables/currencies',
    parent_internal_code: 'NAV.BASE_TABLES.PLATFORM',
    sort_order:           1110,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Currencies',
        description: 'Maintain currency master and translations.' },
      { locale_code: 'EN_GB', display_text: 'Currencies',
        description: 'Maintain currency master and translations.' },
      { locale_code: 'ES_MX', display_text: 'Monedas' },
    ],
  },
  {
    internal_code:        'FXR.RATE.LIST',
    route_path:           '/base-tables/exchange-rates',
    parent_internal_code: 'NAV.BASE_TABLES.PLATFORM',
    sort_order:           1120,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Exchange Rates',
        description: 'View historical FX rates and fetch latest rates from provider.' },
      { locale_code: 'EN_GB', display_text: 'Exchange Rates',
        description: 'View historical FX rates and fetch latest rates from provider.' },
      { locale_code: 'ES_MX', display_text: 'Tipos de Cambio' },
    ],
  },

  // DOC — Report Writer lookup/base tables (BaseApp core module)
  {
    internal_code:        'DOC.FORMAT_MASKS',
    route_path:           '/base-tables/format-masks',
    parent_internal_code: 'NAV.BASE_TABLES.PLATFORM',
    sort_order:           1130,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Format Masks',
        description: 'Maintain numeric, currency, percent, date and phone format masks.' },
      { locale_code: 'EN_GB', display_text: 'Format Masks',
        description: 'Maintain numeric, currency, percent, date and phone format masks.' },
      { locale_code: 'ES_MX', display_text: 'Máscaras de Formato' },
    ],
  },
  {
    internal_code:        'DOC.PAGE_SIZES',
    route_path:           '/base-tables/page-sizes',
    parent_internal_code: 'NAV.BASE_TABLES.PLATFORM',
    sort_order:           1140,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Page Sizes',
        description: 'Maintain report page sizes (A4, Letter, Legal, …) with dimensions.' },
      { locale_code: 'EN_GB', display_text: 'Page Sizes',
        description: 'Maintain report page sizes (A4, Letter, Legal, …) with dimensions.' },
      { locale_code: 'ES_MX', display_text: 'Tamaños de Página' },
    ],
  },
  {
    internal_code:        'DOC.RESOURCES',
    route_path:           '/base-tables/doc-resources',
    parent_internal_code: 'NAV.BASE_TABLES.PLATFORM',
    sort_order:           1150,
    is_active:            'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Static Resources',
        description: 'Maintain shared report resources (logos, text snippets).' },
      { locale_code: 'EN_GB', display_text: 'Static Resources',
        description: 'Maintain shared report resources (logos, text snippets).' },
      { locale_code: 'ES_MX', display_text: 'Recursos Estáticos' },
    ],
  },

  // ===== Per-application pages (owned by derived apps; empty in BaseApp) =====
  ...APP_PAGE_REGISTRY,
];

// A derived app may re-declare an inherited page in its app_pages.ts to
// override placement or activation (e.g. move a platform lookup under its own
// Base Tables sub-group, or deactivate the demo catalog). Dedupe by
// internal_code with the LAST declaration winning, so exactly one definition
// per page reaches csm_sync_page_master — otherwise the earlier core entry
// and the later override would fight inside a single sync pass.
// Merge the build-time per-page release version + date (git-derived, see
// scripts/gen_page_versions.mjs) onto each entry, so the values reach BOTH the
// About box static fallback (pre-auth) AND csm_page_master via csm_sync_page_master
// (the payload is PAGE_REGISTRY — see src/lib/use_page_sync.ts).
export const PAGE_REGISTRY: PageDefinition[] = Array.from(
  new Map(RAW_PAGE_REGISTRY.map((p) => [p.internal_code, p])).values(),
).map((p) => {
  const v = PAGE_VERSIONS[p.internal_code];
  return v ? { ...p, ...v } : p;
});
